/**
 * @fileoverview Comprehensive statistical calculation module for Bitcoin mining pool predictions.
 * Implements Poisson and exponential distributions, growth factor adjustments, deviation analysis,
 * and percentile calculations as documented in /docs/API_CONTEXT.md
 */

/**
 * @typedef {import('../services/dataModels.js').PoolInfo} PoolInfo
 * @typedef {import('../services/dataModels.js').PoolBlock} PoolBlock
 */

// ============================================================================
// 1. Constants & Configuration
// ============================================================================

/** Bitcoin network constants */
export const BLOCK_TIME_SECONDS = 600; // Average block time in seconds
export const BLOCKS_PER_DAY = 144; // Blocks per day (86400 / 600)
export const NETWORK_BLOCK_RATE = 1 / BLOCK_TIME_SECONDS; // λ_network ≈ 0.001667 blocks/second

/** Mathematical constants */
export const E = Math.E; // Euler's number ≈ 2.71828
export const LN_2 = Math.LN2; // Natural log of 2 ≈ 0.693

/** Debug flag for verbose logging (set via DEBUG_POISSON environment variable) */
const DEBUG_POISSON = (typeof process !== 'undefined' && process.env?.DEBUG_POISSON === 'true') ||
  (typeof import.meta !== 'undefined' && import.meta.env?.DEBUG_POISSON === 'true');

/** Percentile z-scores for normal approximation */
export const Z_SCORES = {
  P50: 0, // 50th percentile (median)
  P80: 0.8416, // 80th percentile
  P90: 1.2816, // 90th percentile
  P95: 1.645, // 95th percentile
  P99: 2.326, // 99th percentile
};

// ============================================================================
// 2. Core Mathematical Functions
// ============================================================================

/** Memoization cache for factorial calculations */
const factorialCache = new Map();

/**
 * Calculate factorial n! = n × (n-1) × ... × 2 × 1
 * Uses memoization for performance. Limited to n ≤ 170 for numerical stability.
 * @param {number} n - Non-negative integer
 * @returns {number} Factorial of n
 * @throws {Error} If n < 0 or n > 170
 */
export function factorial(n) {
  if (n < 0 || !Number.isInteger(n)) {
    throw new Error(`Factorial is only defined for non-negative integers, got: ${n}`);
  }
  if (n > 170) {
    throw new Error(`Factorial overflow: n > 170 not supported, got: ${n}`);
  }
  if (n === 0 || n === 1) {
    return 1;
  }
  if (factorialCache.has(n)) {
    return factorialCache.get(n);
  }
  let result = 1;
  for (let i = 2; i <= n; i++) {
    result *= i;
  }
  factorialCache.set(n, result);
  return result;
}

/**
 * Calculate natural logarithm of factorial using Stirling's approximation for large n
 * ln(n!) ≈ n×ln(n) - n + (1/2)×ln(2πn) + 1/(12n)
 * @param {number} n - Non-negative integer
 * @returns {number} Natural logarithm of factorial
 */
export function logFactorial(n) {
  if (n < 0 || !Number.isInteger(n)) {
    throw new Error(`Log factorial is only defined for non-negative integers, got: ${n}`);
  }
  if (n === 0 || n === 1) {
    return 0;
  }
  if (n <= 170) {
    // Use exact factorial for small n
    return Math.log(factorial(n));
  }
  // Stirling's approximation for large n
  const nLogN = n * Math.log(n);
  const correction = 0.5 * Math.log(2 * Math.PI * n) + 1 / (12 * n);
  return nLogN - n + correction;
}

/**
 * Calculate log-gamma function for continuous factorial approximation
 * Uses Stirling's approximation: ln(Γ(x)) ≈ (x-0.5)×ln(x) - x + 0.5×ln(2π)
 * @param {number} x - Positive real number
 * @returns {number} Natural logarithm of gamma function
 */
export function logGamma(x) {
  if (x <= 0) {
    throw new Error(`Log gamma is only defined for positive numbers, got: ${x}`);
  }
  if (Number.isInteger(x) && x <= 170) {
    return logFactorial(x - 1);
  }
  // Stirling's approximation
  const xLogX = (x - 0.5) * Math.log(x);
  const correction = 0.5 * Math.log(2 * Math.PI);
  return xLogX - x + correction;
}

// ============================================================================
// 3. Poisson Distribution Functions
// ============================================================================

/**
 * Poisson Probability Mass Function: P(N = k) = (λ^k × e^(-λ)) / k!
 * Probability of exactly k events occurring in a fixed interval
 * @param {number} k - Number of events (non-negative integer)
 * @param {number} lambda - Rate parameter (expected number of events)
 * @returns {number} Probability of exactly k events (0 ≤ P ≤ 1)
 */
export function poissonPMF(k, lambda) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (lambda === 0) {
    return k === 0 ? 1 : 0;
  }
  if (k < 0 || !Number.isInteger(k)) {
    return 0;
  }
  // Use log-space calculation for numerical stability
  const logLambda = Math.log(lambda);
  const kLogLambda = k * logLambda;
  const negLambda = -lambda;
  const logKFactorial = logFactorial(k);
  const logPMF = kLogLambda + negLambda - logKFactorial;
  return Math.exp(logPMF);
}

/**
 * Poisson Cumulative Distribution Function: P(N ≤ k) = Σ(i=0 to k) PMF(i)
 * Probability of k or fewer events occurring
 * @param {number} k - Number of events (non-negative integer)
 * @param {number} lambda - Rate parameter
 * @returns {number} Probability of k or fewer events (0 ≤ P ≤ 1)
 */
export function poissonCDF(k, lambda) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (lambda === 0) {
    return 1;
  }
  if (k < 0) {
    return 0;
  }
  if (!Number.isFinite(k)) {
    return 1;
  }
  // Sum PMF values from 0 to k
  let sum = 0;
  for (let i = 0; i <= Math.floor(k); i++) {
    sum += poissonPMF(i, lambda);
  }
  // Ensure result is in [0, 1] due to floating-point errors
  return Math.min(1, Math.max(0, sum));
}

/**
 * Poisson Quantile (Inverse CDF): Find the k-th percentile
 * For large λ (λ ≥ 10), uses normal approximation: Q_p ≈ λ + z_p × √λ
 * @param {number} p - Percentile (0 ≤ p ≤ 1)
 * @param {number} lambda - Rate parameter
 * @returns {number} Smallest integer k such that P(N ≤ k) ≥ p
 */
export function poissonQuantile(p, lambda) {
  if (p < 0 || p > 1) {
    throw new Error(`Percentile must be in [0, 1], got: ${p}`);
  }
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (lambda === 0) {
    return 0;
  }
  if (p === 0) {
    return 0;
  }
  if (p === 1) {
    return Infinity;
  }
  // Use normal approximation for large lambda
  if (lambda >= 10) {
    // Map p to z-score (inverse standard normal)
    const z = normalQuantile(p);
    const quantile = lambda + z * Math.sqrt(lambda);
    return Math.max(0, Math.ceil(quantile));
  }
  // For small lambda, use binary search on CDF
  let lower = 0;
  let upper = Math.max(10, Math.ceil(lambda * 3)); // Upper bound
  while (upper - lower > 1) {
    const mid = Math.floor((lower + upper) / 2);
    const cdf = poissonCDF(mid, lambda);
    if (cdf >= p) {
      upper = mid;
    } else {
      lower = mid;
    }
  }
  return poissonCDF(lower, lambda) >= p ? lower : upper;
}

/**
 * Approximate inverse standard normal CDF (quantile function)
 * Simple approximation for z-scores
 * @param {number} p - Percentile (0 < p < 1)
 * @returns {number} Z-score
 */
function normalQuantile(p) {
  // Beasley-Springer-Moro algorithm approximation
  const a = [0, -3.969683028665376e+01, 2.209460984245205e+02, -2.759285104469687e+02, 1.383577518672690e+02, -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [0, -5.447609879822406e+01, 1.615858368580409e+02, -1.556989798598866e+02, 6.680131188771972e+01, -1.328068155288572e+01];
  const c = [0, -7.784894002430293e-03, -3.223964580411365e-01, -2.400758277161838e+00, -2.549732539343734e+00, 4.374664141464968e+00, 2.938163982698783e+00];
  const d = [0, 7.784695709041462e-03, 3.224671290700398e-01, 2.445134137142996e+00, 3.754408661907416e+00];

  if (p === 0.5) return 0;
  if (p < 0.5) return -normalQuantile(1 - p);

  const q = p - 0.5;
  if (q < 0.425) {
    const r = 0.180625 - q * q;
    // Evaluate polynomial: a[7]*r^7 + a[6]*r^6 + ... + a[1]*r using Horner's method
    const polyA = (((((a[7] * r + a[6]) * r + a[5]) * r + a[4]) * r + a[3]) * r + a[2]) * r + a[1];
    const numerator = q * polyA;
    // Evaluate polynomial: b[6]*r^6 + b[5]*r^5 + ... + b[1]*r + 1 using Horner's method
    const polyB = (((((b[6] * r + b[5]) * r + b[4]) * r + b[3]) * r + b[2]) * r + b[1]) * r + 1;
    const denominator = polyB;
    return numerator / denominator;
  } else {
    const r = q < 0.925 ? Math.sqrt(-Math.log(0.5 - q)) : Math.sqrt(-Math.log(1 - p));
    // Evaluate polynomial: c[6]*r^6 + c[5]*r^5 + ... + c[1]*r + c[0] using Horner's method
    const polyC = (((((c[6] * r + c[5]) * r + c[4]) * r + c[3]) * r + c[2]) * r + c[1]) * r + c[0];
    const numerator = polyC;
    // Evaluate polynomial: d[4]*r^4 + d[3]*r^3 + ... + d[1]*r + 1 using Horner's method
    const polyD = ((((d[4] * r + d[3]) * r + d[2]) * r + d[1]) * r + 1);
    const denominator = polyD;
    return numerator / denominator;
  }
}

/**
 * Mean of Poisson distribution
 * @param {number} lambda - Rate parameter
 * @returns {number} Mean (E[N] = λ)
 */
export function poissonMean(lambda) {
  return lambda;
}

/**
 * Standard deviation of Poisson distribution
 * @param {number} lambda - Rate parameter
 * @returns {number} Standard deviation (σ = √λ)
 */
export function poissonStdDev(lambda) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  return Math.sqrt(lambda);
}

// ============================================================================
// 3a. Normal Distribution Functions
// ============================================================================

/**
 * Normal Probability Density Function: f(x) = (1 / (σ * √(2π))) * e^(-0.5 * ((x - μ) / σ)^2)
 * @param {number} x - Value at which to evaluate PDF
 * @param {number} mean - Mean (μ) of the distribution
 * @param {number} stdDev - Standard deviation (σ) of the distribution
 * @returns {number} Probability density at x
 */
export function normalPDF(x, mean, stdDev) {
  if (stdDev <= 0) {
    throw new Error(`Standard deviation must be positive, got: ${stdDev}`);
  }
  if (!Number.isFinite(x) || !Number.isFinite(mean) || !Number.isFinite(stdDev)) {
    return 0;
  }
  const sqrt2Pi = Math.sqrt(2 * Math.PI);
  const coefficient = 1 / (stdDev * sqrt2Pi);
  const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
  return coefficient * Math.exp(exponent);
}

/**
 * Generate normal distribution curve points
 * @param {number} mean - Mean (μ) of the distribution
 * @param {number} stdDev - Standard deviation (σ) of the distribution
 * @param {number} minX - Minimum x value
 * @param {number} maxX - Maximum x value
 * @param {number} [numPoints=200] - Number of points to generate
 * @returns {{x: number, y: number}[]} Array of {x, y} points
 */
export function generateNormalCurve(mean, stdDev, minX, maxX, numPoints = 200) {
  if (stdDev <= 0 || !Number.isFinite(mean) || !Number.isFinite(stdDev)) {
    return [];
  }
  const points = [];
  const step = (maxX - minX) / numPoints;
  for (let i = 0; i <= numPoints; i++) {
    const x = minX + i * step;
    const y = normalPDF(x, mean, stdDev);
    points.push({ x, y });
  }
  return points;
}

// ============================================================================
// 4. Exponential Distribution Functions
// ============================================================================

/**
 * Exponential Probability Density Function: f(t) = λ × e^(-λt)
 * @param {number} t - Time in seconds (t ≥ 0)
 * @param {number} lambda - Rate parameter
 * @returns {number} Probability density at time t
 */
export function exponentialPDF(t, lambda) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (t < 0) {
    return 0;
  }
  if (lambda === 0) {
    return 0;
  }
  return lambda * Math.exp(-lambda * t);
}

/**
 * Exponential Cumulative Distribution Function: P(T ≤ t) = 1 - e^(-λt)
 * Probability that next block arrives within t seconds
 * @param {number} t - Time in seconds (t ≥ 0)
 * @param {number} lambda - Rate parameter
 * @returns {number} Probability (0 ≤ P ≤ 1)
 */
export function exponentialCDF(t, lambda) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (t < 0) {
    return 0;
  }
  if (lambda === 0) {
    return 0;
  }
  return 1 - Math.exp(-lambda * t);
}

/**
 * Exponential Quantile (Inverse CDF): t_p = -ln(1 - p) / λ
 * Time at which p-th percentile of next block arrival occurs
 * @param {number} p - Percentile (0 ≤ p ≤ 1)
 * @param {number} lambda - Rate parameter
 * @returns {number} Time in seconds
 */
export function exponentialQuantile(p, lambda) {
  if (p < 0 || p > 1) {
    throw new Error(`Percentile must be in [0, 1], got: ${p}`);
  }
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (lambda === 0) {
    return Infinity;
  }
  if (p === 0) {
    return 0;
  }
  if (p === 1) {
    return Infinity;
  }
  return -Math.log(1 - p) / lambda;
}

/**
 * Mean of exponential distribution
 * @param {number} lambda - Rate parameter
 * @returns {number} Mean time (E[T] = 1/λ)
 */
export function exponentialMean(lambda) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive for mean calculation, got: ${lambda}`);
  }
  return 1 / lambda;
}

/**
 * Median of exponential distribution
 * @param {number} lambda - Rate parameter
 * @returns {number} Median time (t_median = ln(2)/λ)
 */
export function exponentialMedian(lambda) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive for median calculation, got: ${lambda}`);
  }
  return LN_2 / lambda;
}

// ============================================================================
// 5. Pool Rate Calculations
// ============================================================================

/**
 * Calculate pool block rate: λ_pool = (H_pool / H_network) × λ_network
 * @param {number} poolHashrate - Pool hashrate in H/s
 * @param {number} networkHashrate - Network hashrate in H/s
 * @returns {number} Pool block rate in blocks/second
 */
export function calculatePoolBlockRate(poolHashrate, networkHashrate) {
  if (poolHashrate < 0) {
    throw new Error(`Pool hashrate must be non-negative, got: ${poolHashrate}`);
  }
  if (networkHashrate <= 0) {
    throw new Error(`Network hashrate must be positive, got: ${networkHashrate}`);
  }
  if (poolHashrate === 0) {
    return 0;
  }
  const poolShare = poolHashrate / networkHashrate;
  return poolShare * NETWORK_BLOCK_RATE;
}

/**
 * Calculate pool block rate from hashrate share percentage
 * @param {number} hashrateShare - Pool share of network hashrate (0-1)
 * @returns {number} Pool block rate in blocks/second
 */
export function calculatePoolBlockRateFromShare(hashrateShare) {
  if (hashrateShare < 0 || hashrateShare > 1) {
    throw new Error(`Hashrate share must be in [0, 1], got: ${hashrateShare}`);
  }
  return hashrateShare * NETWORK_BLOCK_RATE;
}

/**
 * Calculate expected number of blocks in time interval: E[N(t)] = λ × t
 * @param {number} lambda - Block rate in blocks/second
 * @param {number} timeSeconds - Time interval in seconds
 * @returns {number} Expected number of blocks
 */
export function calculateExpectedBlocks(lambda, timeSeconds) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (timeSeconds < 0) {
    throw new Error(`Time must be non-negative, got: ${timeSeconds}`);
  }
  return lambda * timeSeconds;
}

/**
 * Calculate expected time to next block: E[T] = 1 / λ
 * @param {number} lambda - Block rate in blocks/second
 * @returns {number} Expected time in seconds
 */
export function calculateExpectedTime(lambda) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive for expected time, got: ${lambda}`);
  }
  return exponentialMean(lambda);
}

// ============================================================================
// 5a. Stable Share Calculation
// ============================================================================

/**
 * Calculate stable share as weighted average of hashrate and block share
 * stable_share = hashrateWeight * hashrateShare + blockShareWeight * blockShare
 * This smooths volatility by combining both metrics, preferring longer-term stability
 * @param {PoolInfo} poolInfo - Pool information object
 * @param {number} [hashrateWeight=0.6] - Weight for hashrate share (default 0.6)
 * @param {number} [blockShareWeight=0.4] - Weight for block share (default 0.4)
 * @returns {number} Stable share value in [0, 1] range
 */
export function calculateStableShare(poolInfo, hashrateWeight = 0.6, blockShareWeight = 0.4) {
  if (!poolInfo) {
    throw new Error('Pool info is required');
  }
  if (typeof poolInfo.hashrateShare !== 'number' || isNaN(poolInfo.hashrateShare)) {
    throw new Error(`Invalid hashrateShare: ${poolInfo.hashrateShare}`);
  }
  
  const hashrateShare = Math.max(0, Math.min(1, poolInfo.hashrateShare));
  const blockShare = poolInfo.blockShare != null && typeof poolInfo.blockShare === 'number' && !isNaN(poolInfo.blockShare)
    ? Math.max(0, Math.min(1, poolInfo.blockShare))
    : null;
  
  // If blockShare is missing (null, undefined, or NaN), fall back to hashrateShare only
  // Note: blockShare === 0 is treated as a valid signal and will participate in the blend
  if (blockShare === null) {
    return hashrateShare;
  }
  
  // Normalize weights to sum to 1
  const totalWeight = hashrateWeight + blockShareWeight;
  const normalizedHashrateWeight = totalWeight > 0 ? hashrateWeight / totalWeight : 0.6;
  const normalizedBlockShareWeight = totalWeight > 0 ? blockShareWeight / totalWeight : 0.4;
  
  // Weighted average
  const stableShare = normalizedHashrateWeight * hashrateShare + normalizedBlockShareWeight * blockShare;
  
  // Clamp to valid range
  return Math.max(0, Math.min(1, stableShare));
}

/**
 * Calculate pool block rate from pool info using stable share
 * @param {PoolInfo} poolInfo - Pool information object
 * @param {number} [hashrateWeight=0.6] - Weight for hashrate share (default 0.6)
 * @param {number} [blockShareWeight=0.4] - Weight for block share (default 0.4)
 * @returns {number} Pool block rate in blocks/second
 */
export function calculatePoolBlockRateFromPoolInfo(poolInfo, hashrateWeight = 0.6, blockShareWeight = 0.4) {
  const stableShare = calculateStableShare(poolInfo, hashrateWeight, blockShareWeight);
  return stableShare * NETWORK_BLOCK_RATE;
}

/**
 * Calculate empirical lambda from actual block intervals
 * 
 * Computes the block rate parameter (λ) directly from observed inter-block intervals,
 * providing a data-driven estimate that reflects the pool's actual historical performance.
 * 
 * Formula: λ_empirical = (n-1) / Σt_i
 * where n = number of blocks, t_i = inter-block intervals
 * 
 * This is equivalent to: λ_empirical = 1 / mean(intervals)
 * 
 * @param {PoolBlock[]} blocks - Array of blocks with timestamp property (should be filtered blocks, non-outliers)
 * @returns {{
 *   lambda: number,              // Empirical lambda (blocks/second)
 *   method: string,              // Calculation method used ("count-based" or "interval-based")
 *   totalSpan: number,           // Total time span in seconds
 *   intervalCount: number,      // Number of intervals (n-1)
 *   meanInterval: number         // Mean inter-block interval in seconds
 * }}
 * @throws {Error} If blocks array is empty or invalid
 */
export function calculateEmpiricalLambda(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    throw new Error('Blocks array must be non-empty for empirical lambda calculation');
  }
  
  // Edge case: single block has no intervals
  if (blocks.length === 1) {
    // Cannot calculate lambda from a single block
    // Return a default value (could be based on network rate or throw error)
    // For now, return 0 to indicate insufficient data
    return {
      lambda: 0,
      method: 'insufficient-data',
      totalSpan: 0,
      intervalCount: 0,
      meanInterval: 0,
    };
  }
  
  // Compute inter-block intervals
  const intervals = computeInterBlockTimes(blocks);
  
  if (intervals.length === 0) {
    // Should not happen if blocks.length > 1, but handle gracefully
    return {
      lambda: 0,
      method: 'insufficient-data',
      totalSpan: 0,
      intervalCount: 0,
      meanInterval: 0,
    };
  }
  
  // Calculate total time span: max(timestamps) - min(timestamps)
  const timestamps = blocks.map(b => b.timestamp).filter(ts => Number.isFinite(ts) && ts > 0);
  if (timestamps.length === 0) {
    throw new Error('No valid timestamps found in blocks');
  }
  
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  const totalSpan = maxTimestamp - minTimestamp;
  
  // Handle edge case: all blocks have identical timestamps
  if (totalSpan === 0) {
    // If all timestamps are identical, intervals should all be 0
    // This is invalid data, return 0 lambda
    return {
      lambda: 0,
      method: 'identical-timestamps',
      totalSpan: 0,
      intervalCount: intervals.length,
      meanInterval: 0,
    };
  }
  
  // Method 1: Count-based (primary method)
  // λ = (n-1) / totalSpan
  // This is the maximum likelihood estimator for exponential distribution
  const intervalCount = intervals.length; // Should be (blocks.length - 1)
  const lambdaCountBased = intervalCount / totalSpan;
  
  // Method 2: Interval-based (validation)
  // λ = 1 / mean(intervals)
  const intervalSum = intervals.reduce((sum, intervalObj) => sum + intervalObj.interval, 0);
  const meanInterval = intervalSum / intervalCount;
  const lambdaIntervalBased = meanInterval > 0 ? 1 / meanInterval : 0;
  
  // Both methods should yield similar results
  // Use count-based as primary (it's more robust to outliers if already filtered)
  const lambda = lambdaCountBased;
  const method = 'count-based';
  
  return {
    lambda,
    method,
    totalSpan,
    intervalCount,
    meanInterval,
  };
}

// ============================================================================
// 6. Growth Factor & Adjustment (Historical Metrics Only)
// ============================================================================
// NOTE: The functions adjustLambda() and calculateAdjustedExpectedTime() are
// DEPRECATED for use in predictions. They violate the memoryless property of
// the exponential distribution by adjusting lambda based on past performance.
// The memoryless property states that past "luck" has no bearing on future
// block discovery probability. Growth factor should only be used for historical
// analysis, not for predictions. Use pure lambda from calculatePoolBlockRateFromPoolInfo()
// instead for all prediction calculations.
// ============================================================================

/**
 * Calculate growth factor from observed vs expected blocks
 * growth_factor = observed / expected
 * @param {number} observedBlocks - Actual blocks found
 * @param {number} expectedBlocks - Expected blocks based on hashrate
 * @returns {number} Growth factor (>1 means outperforming, <1 means underperforming).
 *   Can return Infinity when expectedBlocks=0 and observedBlocks>0.
 *   Non-finite values are treated as "no adjustment" by adjustLambda() and related functions.
 */
export function calculateGrowthFactor(observedBlocks, expectedBlocks) {
  if (expectedBlocks === 0) {
    // If no blocks expected, return 1 (no adjustment)
    return observedBlocks === 0 ? 1 : Infinity;
  }
  if (!Number.isFinite(expectedBlocks)) {
    return 1;
  }
  return observedBlocks / expectedBlocks;
}

/**
 * Adjust lambda based on growth factor: λ_adjusted = λ_expected / growth_factor
 * @deprecated This function violates the memoryless property of the Poisson process.
 *   Growth factor should only be used for historical analysis, not predictions.
 *   Use pure lambda from calculatePoolBlockRateFromPoolInfo() instead.
 * @param {number} lambdaExpected - Expected block rate
 * @param {number} growthFactor - Growth factor from observed performance
 * @returns {number} Adjusted block rate
 */
export function adjustLambda(lambdaExpected, growthFactor) {
  if (lambdaExpected < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambdaExpected}`);
  }
  if (growthFactor <= 0 || !Number.isFinite(growthFactor)) {
    return lambdaExpected; // No adjustment for invalid growth factor
  }
  return lambdaExpected / growthFactor;
}

/**
 * Calculate adjusted expected time: t_adjusted = (1 / λ_expected) × growth_factor
 * @deprecated This function violates the memoryless property of the exponential distribution.
 *   Growth factor should only be used for historical analysis, not predictions.
 *   Use pure expected time from calculateExpectedTime() with lambda from calculatePoolBlockRateFromPoolInfo() instead.
 * @param {number} lambdaExpected - Expected block rate
 * @param {number} growthFactor - Growth factor
 * @returns {number} Adjusted expected time in seconds
 */
export function calculateAdjustedExpectedTime(lambdaExpected, growthFactor) {
  if (lambdaExpected <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambdaExpected}`);
  }
  if (growthFactor <= 0 || !Number.isFinite(growthFactor)) {
    return calculateExpectedTime(lambdaExpected);
  }
  return (1 / lambdaExpected) * growthFactor;
}

// ============================================================================
// 7. Deviation Analysis
// ============================================================================

/**
 * Calculate deviation z-score: z = (observed - expected) / σ
 * @param {number} observedBlocks - Actual blocks found
 * @param {number} expectedBlocks - Expected blocks
 * @param {number} lambda - Block rate
 * @param {number} timeSeconds - Time interval in seconds
 * @returns {number} Z-score (standard deviations from mean)
 */
export function calculateDeviation(observedBlocks, expectedBlocks, lambda, timeSeconds) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (timeSeconds < 0) {
    throw new Error(`Time must be non-negative, got: ${timeSeconds}`);
  }
  const variance = lambda * timeSeconds;
  const stdDev = Math.sqrt(variance);
  if (stdDev === 0) {
    return 0; // No variance, so no deviation
  }
  return (observedBlocks - expectedBlocks) / stdDev;
}

/**
 * Calculate p-value for observing k or more blocks
 * P-value = P(N ≥ k) = 1 - P(N < k) = 1 - P(N ≤ k-1)
 * @param {number} observedBlocks - Number of blocks observed (k)
 * @param {number} lambda - Block rate
 * @param {number} timeSeconds - Time interval in seconds
 * @returns {number} P-value (0 ≤ p ≤ 1)
 */
export function calculatePValue(observedBlocks, lambda, timeSeconds) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (timeSeconds < 0) {
    throw new Error(`Time must be non-negative, got: ${timeSeconds}`);
  }
  if (observedBlocks < 0 || !Number.isInteger(observedBlocks)) {
    return 1; // Invalid observation
  }
  const lambdaT = lambda * timeSeconds;
  if (lambdaT === 0) {
    return observedBlocks === 0 ? 1 : 0;
  }
  // P(N ≥ k) = 1 - P(N ≤ k-1)
  const cdf = poissonCDF(observedBlocks - 1, lambdaT);
  return Math.max(0, Math.min(1, 1 - cdf));
}

/**
 * Check if deviation is statistically significant
 * @param {number} deviation - Z-score (absolute deviation in standard deviations)
 * @param {number} [alpha=0.05] - Significance level
 * @returns {boolean} True if deviation is significant
 */
export function isDeviationSignificant(deviation, alpha = 0.05) {
  if (alpha <= 0 || alpha >= 1) {
    throw new Error(`Alpha must be in (0, 1), got: ${alpha}`);
  }
  // Two-tailed test: |z| > z_{α/2}
  // For α = 0.05: z_{0.025} ≈ 1.96
  const zCritical = normalQuantile(1 - alpha / 2);
  return Math.abs(deviation) > zCritical;
}

// ============================================================================
// 8. Outlier Detection
// ============================================================================

/**
 * Compute inter-block times (intervals) from an array of blocks
 * @param {PoolBlock[]} blocks - Array of blocks with timestamp property
 * @returns {Array<{blockIndex: number, interval: number, block: PoolBlock}>} Array of interval objects
 */
export function computeInterBlockTimes(blocks) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }
  if (blocks.length === 1) {
    return []; // Single block has no intervals
  }
  
  // Sort blocks by timestamp ascending
  const sortedBlocks = [...blocks].sort((a, b) => a.timestamp - b.timestamp);
  
  const intervals = [];
  for (let i = 1; i < sortedBlocks.length; i++) {
    const interval = sortedBlocks[i].timestamp - sortedBlocks[i - 1].timestamp;
    intervals.push({
      blockIndex: i,
      interval: interval,
      block: sortedBlocks[i],
    });
  }
  
  return intervals;
}

/**
 * Detect statistical outliers in block intervals using exponential distribution CDF
 * 
 * Uses iterative refinement: marks intervals in the 5% tails (2.5% each tail) of the
 * exponential distribution, then refits lambda after removing outliers and repeats
 * until convergence or max iterations.
 * 
 * Mathematical approach:
 * - For each interval t, compute CDF: P(T ≤ t) = 1 - e^(-λt)
 * - Mark as outlier if: P(T ≤ t) < α/2 (too short) or P(T ≤ t) > 1 - α/2 (too long)
 *   where α = pThreshold (default 0.05 for 5% tails)
 * - After marking outliers, refit lambda: λ_new = (non-outlier count) / Σ(non-outlier intervals)
 * - Iterate until outlier set stabilizes or maxIterations reached
 * 
 * @param {PoolBlock[]} blocks - Array of blocks with timestamp property
 * @param {number} lambda - Initial rate parameter (blocks/second)
 * @param {number} [pThreshold=0.05] - Probability threshold for tails (0 < pThreshold < 1)
 *   Default 0.05 means 5% of intervals are marked as outliers (2.5% in each tail)
 * @param {number} [maxIterations=3] - Maximum number of refinement iterations
 * @returns {{
 *   blocks: PoolBlock[], // Blocks enriched with isOutlier boolean property
 *   outliersCount: number, // Number of blocks marked as outliers
 *   refinedLambda: number, // Lambda after outlier removal and refitting
 *   iterations: number // Number of iterations performed
 * }}
 * @throws {Error} If blocks array is empty, lambda ≤ 0, or pThreshold not in (0, 1)
 */
export function detectOutliers(blocks, lambda, pThreshold = 0.05, maxIterations = 3) {
  // Input validation
  if (!Array.isArray(blocks) || blocks.length === 0) {
    throw new Error('Blocks array must be non-empty');
  }
  if (lambda <= 0 || !Number.isFinite(lambda)) {
    throw new Error(`Lambda must be positive and finite, got: ${lambda}`);
  }
  if (pThreshold <= 0 || pThreshold >= 1) {
    throw new Error(`pThreshold must be in (0, 1), got: ${pThreshold}`);
  }
  if (!Number.isInteger(maxIterations) || maxIterations < 1) {
    throw new Error(`maxIterations must be a positive integer, got: ${maxIterations}`);
  }
  
  // Edge case: single block has no intervals, so no outliers possible
  if (blocks.length === 1) {
    const enrichedBlocks = blocks.map(block => ({ ...block, isOutlier: false }));
    return {
      blocks: enrichedBlocks,
      outliersCount: 0,
      refinedLambda: lambda,
      iterations: 0,
    };
  }
  
  // Compute inter-block times
  const intervals = computeInterBlockTimes(blocks);
  if (intervals.length === 0) {
    // Should not happen if blocks.length > 1, but handle gracefully
    const enrichedBlocks = blocks.map(block => ({ ...block, isOutlier: false }));
    return {
      blocks: enrichedBlocks,
      outliersCount: 0,
      refinedLambda: lambda,
      iterations: 0,
    };
  }
  
  // Create a map from block to its interval (for marking outliers)
  // blocks[i] corresponds to interval[i-1] (first block has no prior interval)
  const blockToIntervalMap = new Map();
  for (let i = 0; i < intervals.length; i++) {
    blockToIntervalMap.set(intervals[i].block, intervals[i]);
  }
  
  let currentLambda = lambda;
  let outlierIntervals = new Set();
  let previousOutlierSet = null;
  let iterations = 0;
  
  // Iterative refinement
  for (let iter = 0; iter < maxIterations; iter++) {
    iterations = iter + 1;
    previousOutlierSet = new Set(outlierIntervals);
    outlierIntervals = new Set();
    
    // Mark outliers using current lambda
    const lowerThreshold = pThreshold / 2;
    const upperThreshold = 1 - pThreshold / 2;
    
    for (const intervalObj of intervals) {
      const cdf = exponentialCDF(intervalObj.interval, currentLambda);
      if (cdf < lowerThreshold || cdf > upperThreshold) {
        outlierIntervals.add(intervalObj);
      }
    }
    
    // Check for convergence (outlier set hasn't changed)
    if (previousOutlierSet.size === outlierIntervals.size) {
      let allMatch = true;
      for (const interval of outlierIntervals) {
        if (!previousOutlierSet.has(interval)) {
          allMatch = false;
          break;
        }
      }
      if (allMatch) {
        break; // Converged
      }
    }
    
    // Refit lambda using non-outlier intervals
    const nonOutlierIntervals = intervals.filter(interval => !outlierIntervals.has(interval));
    if (nonOutlierIntervals.length === 0) {
      // All intervals are outliers - stop iteration
      break;
    }
    
    const totalTime = nonOutlierIntervals.reduce((sum, interval) => sum + interval.interval, 0);
    if (totalTime > 0) {
      currentLambda = nonOutlierIntervals.length / totalTime;
    } else {
      // All intervals are zero - cannot refit, use current lambda
      break;
    }
  }
  
  // Find the chronologically earliest block (by timestamp) to ensure it's never an outlier
  const earliestBlock = blocks.reduce((earliest, block) => {
    if (!earliest || block.timestamp < earliest.timestamp) {
      return block;
    }
    return earliest;
  }, null);
  
  // Enrich blocks with isOutlier flag using blockToIntervalMap for correct mapping
  // The chronologically earliest block is never an outlier
  const enrichedBlocks = blocks.map((block) => {
    // Force earliest block to never be an outlier
    if (earliestBlock && block === earliestBlock) {
      return { ...block, isOutlier: false };
    }
    // Use blockToIntervalMap to get the interval associated with this block
    const intervalObj = blockToIntervalMap.get(block);
    const isOutlier = intervalObj ? outlierIntervals.has(intervalObj) : false;
    return { ...block, isOutlier };
  });
  
  // Count outliers
  const outliersCount = enrichedBlocks.filter(block => block.isOutlier).length;
  
  return {
    blocks: enrichedBlocks,
    outliersCount,
    refinedLambda: currentLambda,
    iterations,
  };
}

// ============================================================================
// 9. Percentile & Confidence Interval Functions
// ============================================================================

/**
 * Calculate time percentiles for next block arrival
 * @param {number} lambda - Block rate
 * @param {number[]} [percentiles=[0.5, 0.8, 0.9, 0.95]] - Array of percentile values (0-1). Defaults to [0.5, 0.8, 0.9, 0.95] (50th, 80th, 90th, 95th percentiles)
 * @returns {number[]} Array of time percentiles in seconds
 */
export function calculateTimePercentiles(lambda, percentiles = [0.5, 0.8, 0.9, 0.95]) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  return percentiles.map(p => exponentialQuantile(p, lambda));
}

/**
 * Calculate block count percentiles for given time interval
 * @param {number} lambda - Block rate
 * @param {number} timeSeconds - Time interval in seconds
 * @param {number[]} [percentiles=[0.5, 0.8, 0.9, 0.95]] - Array of percentile values (0-1). Defaults to [0.5, 0.8, 0.9, 0.95] (50th, 80th, 90th, 95th percentiles)
 * @returns {number[]} Array of block count percentiles
 */
export function calculateBlockCountPercentiles(lambda, timeSeconds, percentiles = [0.5, 0.8, 0.9, 0.95]) {
  if (lambda < 0) {
    throw new Error(`Lambda must be non-negative, got: ${lambda}`);
  }
  if (timeSeconds < 0) {
    throw new Error(`Time must be non-negative, got: ${timeSeconds}`);
  }
  const lambdaT = lambda * timeSeconds;
  return percentiles.map(p => poissonQuantile(p, lambdaT));
}

/**
 * Calculate confidence interval for time to next block
 * @param {number} lambda - Block rate
 * @param {number} [confidenceLevel=0.9] - Confidence level (e.g., 0.9 for 90%)
 * @returns {{lower: number, upper: number}} Confidence interval bounds in seconds
 */
export function calculateConfidenceInterval(lambda, confidenceLevel = 0.9) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  if (confidenceLevel <= 0 || confidenceLevel >= 1) {
    throw new Error(`Confidence level must be in (0, 1), got: ${confidenceLevel}`);
  }
  const alpha = 1 - confidenceLevel;
  const lower = exponentialQuantile(alpha / 2, lambda);
  const upper = exponentialQuantile(1 - alpha / 2, lambda);
  return { lower, upper };
}

// ============================================================================
// 10. Probability Curve Generation
// ============================================================================

/**
 * Generate probability density curve for exponential distribution
 * @param {number} lambda - Block rate
 * @param {number} maxTime - Maximum time in seconds
 * @param {number} [numPoints=100] - Number of points to generate
 * @returns {{time: number, probability: number}[]} Array of {time, probability} points
 */
export function generateProbabilityCurve(lambda, maxTime, numPoints = 100) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  if (maxTime <= 0) {
    throw new Error(`Max time must be positive, got: ${maxTime}`);
  }
  const points = [];
  const step = maxTime / numPoints;
  for (let i = 0; i <= numPoints; i++) {
    const time = i * step;
    const probability = exponentialPDF(time, lambda);
    points.push({ time, probability });
  }
  return points;
}

/**
 * Generate cumulative probability curve for exponential distribution
 * @param {number} lambda - Block rate
 * @param {number} maxTime - Maximum time in seconds
 * @param {number} [numPoints=100] - Number of points to generate
 * @returns {{time: number, cumulativeProbability: number}[]} Array of {time, cumulativeProbability} points
 */
export function generateCumulativeProbabilityCurve(lambda, maxTime, numPoints = 100) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  if (maxTime <= 0) {
    throw new Error(`Max time must be positive, got: ${maxTime}`);
  }
  const points = [];
  const step = maxTime / numPoints;
  for (let i = 0; i <= numPoints; i++) {
    const time = i * step;
    const cumulativeProbability = exponentialCDF(time, lambda);
    points.push({ time, cumulativeProbability });
  }
  return points;
}

/**
 * Generate deviation bands (confidence intervals) around probability curve
 * Creates bands around the main PDF curve by using lambda values adjusted based on confidence intervals.
 * The bands represent uncertainty in the PDF itself, forming a tube around the central curve.
 * @param {number} lambda - Block rate
 * @param {number} maxTime - Maximum time in seconds
 * @param {number} [numPoints=100] - Number of points to generate
 * @param {number} [confidenceLevel=0.9] - Confidence level for bands
 * @returns {{upper: {time: number, probability: number}[], lower: {time: number, probability: number}[]}} Upper and lower probability bands
 */
export function generateDeviationBands(lambda, maxTime, numPoints = 100, confidenceLevel = 0.9) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  if (maxTime <= 0) {
    throw new Error(`Max time must be positive, got: ${maxTime}`);
  }
  // Get confidence interval for time to derive lambda scaling factors
  const { lower: lowerTime, upper: upperTime } = calculateConfidenceInterval(lambda, confidenceLevel);
  // Derive lambda bounds: use the ratio of expected time to CI bounds
  // This creates bands that show what the PDF would look like at different rates
  const expectedTime = 1 / lambda;
  // Lambda for upper time bound (slower rate): λ_upper = 1/t_U
  // Lambda for lower time bound (faster rate): λ_lower = 1/t_L
  const lambdaUpper = 1 / upperTime; // Slower rate (longer wait)
  const lambdaLower = 1 / lowerTime; // Faster rate (shorter wait)
  const step = maxTime / numPoints;
  const upper = [];
  const lower = [];
  for (let i = 0; i <= numPoints; i++) {
    const time = i * step;
    // Upper band: PDF at slower rate (lambdaUpper) - higher PDF at early times
    // Lower band: PDF at faster rate (lambdaLower) - lower PDF at early times
    const upperProb = exponentialPDF(time, lambdaUpper);
    const lowerProb = exponentialPDF(time, lambdaLower);
    upper.push({ time, probability: upperProb });
    lower.push({ time, probability: lowerProb });
  }
  return { upper, lower };
}

// ============================================================================
// 11. High-Level Analysis Functions
// ============================================================================

/**
 * Analyze pool performance with comprehensive metrics
 * 
 * Uses empirical lambda (calculated from all filtered block intervals) for predictions by default.
 * The hashrate-based lambda is retained as a comparison metric to show how empirical model differs
 * from theoretical expectations.
 * 
 * The 24-hour window is used only for historical metrics (observedBlocks, expectedBlocks, growthFactor, deviation),
 * while empirical lambda is calculated from ALL blocks (not just 24-hour window) for more accurate predictions.
 * 
 * @param {PoolBlock[]} blocks - Array of blocks mined by pool (should be filtered blocks, non-outliers)
 * @param {PoolInfo} poolInfo - Pool information object
 * @param {number} [analysisWindowHours=24] - Analysis window in hours (for historical metrics only)
 * @param {number} [hashrateWeight=0.6] - Weight for hashrate share in stable share calculation (default 0.6)
 * @param {number} [blockShareWeight=0.4] - Weight for block share in stable share calculation (default 0.4)
 * @param {boolean} [useEmpiricalLambda=true] - If true, use empirical lambda from all blocks for predictions (default true)
 * @returns {{
 *   // Prediction parameters (using empirical lambda by default)
 *   lambdaEmpirical: number,        // Lambda calculated from all block intervals
 *   lambdaHashrate: number,         // Lambda calculated from poolInfo (comparison metric)
 *   lambdaUsed: number,             // Lambda actually used for predictions (equals lambdaEmpirical when useEmpiricalLambda=true)
 *   expectedTime: number,
 *   medianTime: number,
 *   timePercentiles: number[],      // [50%, 80%, 90%, 95%]
 *   blockCountPercentiles: number[], // [50%, 80%, 90%, 95%]
 *   empiricalStats: {               // Statistics from empirical lambda calculation
 *     totalSpan: number,
 *     intervalCount: number,
 *     meanInterval: number
 *   },
 *   // Historical metrics (for analysis only, not predictions, uses 24-hour window)
 *   observedBlocks: number,
 *   expectedBlocks: number,
 *   growthFactor: number,
 *   deviation: {zScore: number, pValue: number},  // Uses lambdaHashrate (consistent with expectedBlocks)
 *   isSignificant: boolean,
 *   // Optional empirical-based metrics for comparison (null if lambdaEmpirical <= 0)
 *   deviationEmpirical: number | null,  // Deviation using empirical lambda and expectedBlocksEmpirical
 *   pValueEmpirical: number | null      // P-value using empirical lambda
 * }} Analysis results object
 */
export function analyzePoolPerformance(blocks, poolInfo, analysisWindowHours = 24, hashrateWeight = 0.6, blockShareWeight = 0.4, useEmpiricalLambda = true) {
  if (!poolInfo) {
    throw new Error('Invalid pool info: poolInfo is required');
  }
  if (typeof poolInfo.hashrateShare !== 'number' || isNaN(poolInfo.hashrateShare)) {
    throw new Error(`Invalid pool info: hashrateShare must be a number, got: ${poolInfo.hashrateShare} (${typeof poolInfo.hashrateShare})`);
  }
  if (!Array.isArray(blocks)) {
    throw new Error('Blocks must be an array');
  }
  const analysisWindowSeconds = analysisWindowHours * 3600;
  const currentTime = Math.floor(Date.now() / 1000);
  const windowStartTime = currentTime - analysisWindowSeconds;
  
  // Filter blocks within analysis window
  const blocksInWindow = blocks.filter(block => block.timestamp >= windowStartTime);
  const observedBlocks = blocksInWindow.length;
  
  // Calculate expected blocks based on hashrate share (for historical comparison)
  // NOTE: expectedBlocks and growthFactor use hashrateShare exclusively, not stable_share.
  // This is intentional: historical performance metrics compare observed blocks against
  // the pure hashrate-based expectation, while lambda (used for predictions) uses the
  // smoothed stable_share that blends hashrate and block share. This split allows us to
  // track pure hashrate performance separately from the prediction model.
  const expectedBlocksInWindow = poolInfo.hashrateShare * BLOCKS_PER_DAY * (analysisWindowHours / 24);
  const expectedBlocks = expectedBlocksInWindow;
  
  // Calculate both lambdas for comparison
  // 1. Empirical lambda: calculated from ALL filtered block intervals (data-driven)
  let lambdaEmpirical = 0;
  let empiricalStats = { totalSpan: 0, intervalCount: 0, meanInterval: 0 };
  
  try {
    if (blocks.length > 0) {
      const empiricalResult = calculateEmpiricalLambda(blocks);
      lambdaEmpirical = empiricalResult.lambda;
      empiricalStats = {
        totalSpan: empiricalResult.totalSpan,
        intervalCount: empiricalResult.intervalCount,
        meanInterval: empiricalResult.meanInterval,
      };
    }
  } catch (err) {
    if (DEBUG_POISSON) {
      console.warn('Failed to calculate empirical lambda:', err);
    }
    // Fall back to hashrate-based lambda if empirical calculation fails
    lambdaEmpirical = 0;
  }
  
  // 2. Hashrate-based lambda: calculated from poolInfo (theoretical)
  const lambdaHashrate = calculatePoolBlockRateFromPoolInfo(poolInfo, hashrateWeight, blockShareWeight);
  
  // Determine which lambda to use for predictions
  const lambdaUsed = useEmpiricalLambda && lambdaEmpirical > 0 ? lambdaEmpirical : lambdaHashrate;
  
  // Calculate growth factor (historical metric only, not used for predictions)
  const growthFactor = calculateGrowthFactor(observedBlocks, expectedBlocks);
  
  // Handle zero lambda case (pool has no hashrate and no blocks)
  if (lambdaUsed <= 0) {
    if (DEBUG_POISSON) {
      console.warn('Zero lambda detected - returning Infinity values');
    }
      // Return default values for pools with no hashrate
      return {
        // Prediction parameters
        lambdaEmpirical,
        lambdaHashrate,
        lambdaUsed: 0,
        expectedTime: Infinity,
        medianTime: Infinity,
        timePercentiles: [Infinity, Infinity, Infinity, Infinity],
        blockCountPercentiles: [0, 0, 0, 0],
        empiricalStats,
      // Historical metrics
      observedBlocks,
      expectedBlocks,
      growthFactor: 1,
      deviation: {
        zScore: 0,
        pValue: 1,
      },
      isSignificant: false,
      // Empirical-based metrics (null when lambdaEmpirical <= 0)
      deviationEmpirical: null,
      pValueEmpirical: null,
    };
  }
  
  // Calculate expected and median times using lambdaUsed (empirical by default)
  const expectedTime = calculateExpectedTime(lambdaUsed);
  const medianTime = exponentialMedian(lambdaUsed);
  
  // Calculate deviation and p-value using lambdaHashrate for consistency with expectedBlocks
  // expectedBlocks is based on hashrate, so deviation/p-value must use lambdaHashrate to maintain Poisson consistency
  const expectedBlocksHashrate = expectedBlocks; // Already calculated from hashrate
  const deviationZScore = calculateDeviation(observedBlocks, expectedBlocksHashrate, lambdaHashrate, analysisWindowSeconds);
  const pValue = calculatePValue(observedBlocks, lambdaHashrate, analysisWindowSeconds);
  const isSignificant = isDeviationSignificant(deviationZScore);
  
  // Optionally calculate empirical-based metrics for comparison
  let deviationEmpirical = null;
  let pValueEmpirical = null;
  if (lambdaEmpirical > 0) {
    const expectedBlocksEmpirical = lambdaEmpirical * analysisWindowSeconds;
    deviationEmpirical = calculateDeviation(observedBlocks, expectedBlocksEmpirical, lambdaEmpirical, analysisWindowSeconds);
    pValueEmpirical = calculatePValue(observedBlocks, lambdaEmpirical, analysisWindowSeconds);
  }
  
  // Calculate percentiles using lambdaUsed (empirical by default)
  const timePercentiles = calculateTimePercentiles(lambdaUsed, [0.5, 0.8, 0.9, 0.95]);
  const blockCountPercentiles = calculateBlockCountPercentiles(lambdaUsed, analysisWindowSeconds, [0.5, 0.8, 0.9, 0.95]);
  const result = {
    // Prediction parameters (using empirical lambda by default)
    lambdaEmpirical,
    lambdaHashrate,
    lambdaUsed,
    expectedTime,
    medianTime,
    timePercentiles,
    blockCountPercentiles,
    empiricalStats,
    // Historical metrics (for analysis only, not predictions, uses 24-hour window)
    observedBlocks,
    expectedBlocks,
    growthFactor,
    deviation: {
      zScore: deviationZScore,
      pValue: pValue,
    },
    isSignificant,
    // Optional empirical-based metrics for comparison (null if lambdaEmpirical <= 0)
    deviationEmpirical,
    pValueEmpirical,
  };
  
  return result;
}

/**
 * Calculate probabilities for next block within various time windows
 * @param {number} lambda - Block rate
 * @param {number} timeSinceLastBlock - Time since last block in seconds
 * @returns {{
 *   within1h: number,
 *   within2h: number,
 *   within4h: number,
 *   within8h: number,
 *   within12h: number,
 *   within24h: number
 * }} Probabilities for each time window
 */
export function calculateNextBlockProbabilities(lambda, timeSinceLastBlock) {
  if (lambda <= 0) {
    throw new Error(`Lambda must be positive, got: ${lambda}`);
  }
  if (timeSinceLastBlock < 0) {
    throw new Error(`Time since last block must be non-negative, got: ${timeSinceLastBlock}`);
  }
  // Due to memoryless property: P(T ≤ t | T > s) = P(T ≤ t-s)
  // Probability within next hour = P(T ≤ timeSinceLastBlock + 3600) - P(T ≤ timeSinceLastBlock)
  const windows = [
    { name: 'within1h', seconds: 3600 },
    { name: 'within2h', seconds: 7200 },
    { name: 'within4h', seconds: 14400 },
    { name: 'within8h', seconds: 28800 },
    { name: 'within12h', seconds: 43200 },
    { name: 'within24h', seconds: 86400 },
  ];
  const result = {};
  for (const window of windows) {
    const totalTime = timeSinceLastBlock + window.seconds;
    const probTotal = exponentialCDF(totalTime, lambda);
    const probAlready = exponentialCDF(timeSinceLastBlock, lambda);
    // Conditional probability: P(T ≤ total | T > since) = (P(total) - P(since)) / (1 - P(since))
    const probConditional = probAlready === 1 ? 1 : (probTotal - probAlready) / (1 - probAlready);
    result[window.name] = Math.max(0, Math.min(1, probConditional));
  }
  return result;
}

/**
 * Generate all data needed for UI visualization
 * @param {PoolBlock[]} blocks - Array of blocks
 * @param {PoolInfo} poolInfo - Pool information
 * @param {number} [currentTime] - Current time (Unix timestamp, defaults to now)
 * @param {number} [analysisWindowHours=24] - Analysis window in hours for performance analysis
 * @param {number} [hashrateWeight=0.6] - Weight for hashrate share in stable share calculation (default 0.6)
 * @param {number} [blockShareWeight=0.4] - Weight for block share in stable share calculation (default 0.4)
 * @param {number} [confidenceLevel=0.9] - Confidence level for deviation bands (default 0.9)
 * @returns {{
 *   analysis: ReturnType<typeof analyzePoolPerformance>,
 *   probabilityCurve: ReturnType<typeof generateProbabilityCurve>,
 *   cumulativeCurve: ReturnType<typeof generateCumulativeProbabilityCurve>,
 *   deviationBands: ReturnType<typeof generateDeviationBands>,
 *   nextBlockProbabilities: ReturnType<typeof calculateNextBlockProbabilities>,
 *   timeSinceLastBlock: number,
 *   statistics: {
 *     mean: number,
 *     median: number,
 *     stdDev: number,
 *     confidenceInterval: {lower: number, upper: number}
 *   }
 * }} Complete prediction data object
 */
export function generatePredictionData(blocks, poolInfo, currentTime = Math.floor(Date.now() / 1000), analysisWindowHours = 24, hashrateWeight = 0.6, blockShareWeight = 0.4, confidenceLevel = 0.9) {
  if (!poolInfo || typeof poolInfo.hashrateShare !== 'number') {
    throw new Error('Invalid pool info: hashrateShare required');
  }
  if (!Array.isArray(blocks)) {
    throw new Error('Blocks must be an array');
  }
  
  // Analyze performance with empirical lambda (default)
  const analysis = analyzePoolPerformance(blocks, poolInfo, analysisWindowHours, hashrateWeight, blockShareWeight, true);
  
  // Use lambdaUsed from analysis (which is empirical lambda by default)
  const lambda = analysis.lambdaUsed;
  
  // Handle zero lambda case (pool has no hashrate)
  if (lambda <= 0) {
    if (DEBUG_POISSON) {
      console.warn('Zero lambda in generatePredictionData - returning empty curves');
    }
    // Return default values for pools with no hashrate
    const lastBlockTimestamp = poolInfo.lastBlockTimestamp || (blocks.length > 0 ? Math.max(...blocks.map(b => b.timestamp)) : 0);
    const timeSinceLastBlock = getTimeSinceLastBlock(lastBlockTimestamp, currentTime);
    return {
      analysis,
      probabilityCurve: [],
      cumulativeCurve: [],
      deviationBands: { upper: [], lower: [] },
      nextBlockProbabilities: {
        within1h: 0,
        within2h: 0,
        within4h: 0,
        within8h: 0,
        within12h: 0,
        within24h: 0,
      },
      timeSinceLastBlock,
      statistics: {
        mean: Infinity,
        median: Infinity,
        stdDev: Infinity,
        confidenceInterval: { lower: Infinity, upper: Infinity },
      },
    };
  }
  
  // Calculate time since last block
  const lastBlockTimestamp = poolInfo.lastBlockTimestamp || (blocks.length > 0 ? Math.max(...blocks.map(b => b.timestamp)) : 0);
  const timeSinceLastBlock = getTimeSinceLastBlock(lastBlockTimestamp, currentTime);
  
  // Generate curves (use 4x expected time as max for visualization)
  const maxTime = Math.max(analysis.expectedTime * 4, 86400); // At least 24 hours
  
  const probabilityCurve = generateProbabilityCurve(lambda, maxTime, 200);
  const cumulativeCurve = generateCumulativeProbabilityCurve(lambda, maxTime, 200);
  const deviationBands = generateDeviationBands(lambda, maxTime, 200, confidenceLevel);
  
  // Calculate next block probabilities
  const nextBlockProbabilities = calculateNextBlockProbabilities(lambda, timeSinceLastBlock);
  
  // Calculate statistics (using pure exponential model)
  const mean = exponentialMean(lambda);
  const median = analysis.medianTime; // Use median from analysis
  const stdDev = Math.sqrt(1 / (lambda * lambda)); // Standard deviation of exponential = 1/λ
  const confidenceInterval = calculateConfidenceInterval(lambda, confidenceLevel);
  const result = {
    analysis,
    probabilityCurve,
    cumulativeCurve,
    deviationBands,
    nextBlockProbabilities,
    timeSinceLastBlock,
    statistics: {
      mean,
      median,
      stdDev,
      confidenceInterval,
    },
  };
  
  return result;
}

// ============================================================================
// 12. Utility Functions
// ============================================================================

/**
 * Format probability as percentage string
 * @param {number} probability - Probability value (0-1)
 * @param {number} [decimals=2] - Number of decimal places
 * @returns {string} Formatted percentage (e.g., "45.67%")
 */
export function formatProbability(probability) {
  if (!Number.isFinite(probability)) {
    return '0%';
  }
  const percentage = Math.max(0, Math.min(100, probability * 100));
  return `${percentage.toFixed(2)}%`;
}

/**
 * Format time interval in seconds to human-readable string
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string (e.g., "2h 15m" or "45s")
 */
export function formatTimeInterval(seconds) {
  // Treat non-finite or negative values as zero
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0s';
  }
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
  return parts.join(' ');
}

/**
 * Calculate elapsed time since last block
 * @param {number} lastBlockTimestamp - Unix timestamp of last block (seconds)
 * @param {number} [currentTime] - Current Unix timestamp (seconds, defaults to now)
 * @returns {number} Elapsed time in seconds
 */
export function getTimeSinceLastBlock(lastBlockTimestamp, currentTime = Math.floor(Date.now() / 1000)) {
  if (!Number.isFinite(lastBlockTimestamp) || lastBlockTimestamp <= 0) {
    return 0;
  }
  if (!Number.isFinite(currentTime) || currentTime <= 0) {
    currentTime = Math.floor(Date.now() / 1000);
  }
  const elapsed = currentTime - lastBlockTimestamp;
  return Math.max(0, elapsed);
}

/**
 * Calculate network hashrate from difficulty
 * Formula from dataModels.js: H_network ≈ difficulty × 2^32 / 600
 * @param {number} difficulty - Network difficulty
 * @returns {number} Network hashrate in H/s
 */
export function getNetworkHashrateFromDifficulty(difficulty) {
  if (!Number.isFinite(difficulty) || difficulty <= 0) {
    return 0;
  }
  return (difficulty * Math.pow(2, 32)) / 600;
}

// ============================================================================
// 13. Backtesting Functions
// ============================================================================

/**
 * Helper function to get worst predictions (largest absolute errors)
 * @param {Array} errors - Array of error objects from backtestAccuracy
 * @param {number} count - Number of worst predictions to return (default 5)
 * @returns {Array} Top N worst predictions sorted by absolute error descending
 */
export function getWorstPredictions(errors, count = 5) {
  if (!Array.isArray(errors) || errors.length === 0) {
    return [];
  }
  return [...errors]
    .sort((a, b) => b.absError - a.absError)
    .slice(0, count);
}

/**
 * Backtest model accuracy by comparing historical predictions against actual block intervals
 * 
 * Simulates real-time predictions by iterating through blocks chronologically, using only
 * prior blocks to compute empirical lambda for each prediction. This avoids lookahead bias
 * and provides a realistic assessment of model performance.
 * 
 * Mathematical formulas:
 * - Predicted median: median = ln(2)/λ
 * - Mean Absolute Error: MAE = (1/n)Σ|eᵢ|
 * - Root Mean Square Error: RMSE = sqrt((1/n)Σeᵢ²)
 * - Calibration: (count(actualᵢ < predictedᵢ) / n) * 100
 * 
 * For a well-calibrated exponential distribution, calibration should be approximately 50%,
 * meaning about half of actual intervals are below the predicted median.
 * 
 * @param {PoolBlock[]} filteredBlocks - Array of blocks (outliers removed), sorted by timestamp descending
 * @param {Function} lambdaFunc - Function `(priorBlocks) => lambda` that computes empirical lambda from prior blocks only
 * @returns {{
 *   metrics: {
 *     mae: number,              // Mean Absolute Error in seconds
 *     rmse: number,            // Root Mean Square Error in seconds
 *     calibration: number,      // Percentage of actuals < predicted median (0-100)
 *     totalPredictions: number, // Number of successful predictions
 *     meanError: number        // Average signed error (bias indicator) in seconds
 *   },
 *   errors: Array<{
 *     blockHeight: number,
 *     blockTimestamp: number,
 *     actualInterval: number,
 *     predictedMedian: number,
 *     error: number,
 *     percentError: number,
 *     absError: number
 *   }>,
 *   summary: {
 *     bestPrediction: Object | null,
 *     worstPrediction: Object | null,
 *     medianError: number
 *   }
 * } | null} Backtesting results, or null if insufficient data
 * 
 * @example
 * // Define lambda function that computes empirical lambda from prior blocks
 * const lambdaFunc = (priorBlocks) => {
 *   if (priorBlocks.length < 2) return null;
 *   const empiricalStats = calculateEmpiricalLambda(priorBlocks);
 *   return empiricalStats?.lambda || null;
 * };
 * 
 * const results = backtestAccuracy(filteredBlocks, lambdaFunc);
 * console.log('MAE:', results.metrics.mae);
 * console.log('RMSE:', results.metrics.rmse);
 * console.log('Calibration:', results.metrics.calibration + '%');
 */
export function backtestAccuracy(filteredBlocks, lambdaFunc) {
  // Input validation
  if (!Array.isArray(filteredBlocks) || filteredBlocks.length < 2) {
    if (DEBUG_POISSON) {
      console.warn('Insufficient blocks for backtesting:', filteredBlocks.length);
    }
    return null;
  }
  
  if (typeof lambdaFunc !== 'function') {
    throw new Error('lambdaFunc must be a function');
  }
  
  // Sort blocks chronologically (oldest first) for forward iteration
  const sortedBlocks = [...filteredBlocks].sort((a, b) => a.timestamp - b.timestamp);
  
  // Validate timestamps are monotonically increasing
  for (let i = 1; i < sortedBlocks.length; i++) {
    if (sortedBlocks[i].timestamp <= sortedBlocks[i - 1].timestamp) {
      if (DEBUG_POISSON) {
        console.warn('Non-monotonic timestamps detected, skipping backtesting');
      }
      return null;
    }
  }
  
  const errors = [];
  
  // For each block i (starting from index 1, since block 0 has no prior interval)
  for (let i = 1; i < sortedBlocks.length; i++) {
    // Extract prior blocks (all blocks before current)
    const priorBlocks = sortedBlocks.slice(0, i);
    
    // Compute lambda from prior blocks only (no lookahead bias)
    let lambdaPredicted = null;
    try {
      lambdaPredicted = lambdaFunc(priorBlocks);
    } catch (err) {
      if (DEBUG_POISSON) {
        console.warn(`Lambda function failed for block ${sortedBlocks[i].height}:`, err);
      }
      continue; // Skip this prediction
    }
    
    // Validate lambda
    if (!Number.isFinite(lambdaPredicted) || lambdaPredicted <= 0) {
      if (DEBUG_POISSON) {
        console.warn(`Invalid lambda for block ${sortedBlocks[i].height}:`, lambdaPredicted);
      }
      continue; // Skip this prediction
    }
    
    // Calculate predicted median: median = ln(2)/λ
    const predictedMedian = exponentialMedian(lambdaPredicted);
    
    // Compute actual interval
    const actualInterval = sortedBlocks[i].timestamp - sortedBlocks[i - 1].timestamp;
    
    // Validate actual interval
    if (!Number.isFinite(actualInterval) || actualInterval <= 0) {
      if (DEBUG_POISSON) {
        console.warn(`Invalid actual interval for block ${sortedBlocks[i].height}:`, actualInterval);
      }
      continue; // Skip this prediction
    }
    
    // Calculate error: actual - predicted (positive = underestimated, negative = overestimated)
    const error = actualInterval - predictedMedian;
    
    // Calculate percent error
    const percentError = (error / actualInterval) * 100;
    
    // Store per-block result
    errors.push({
      blockHeight: sortedBlocks[i].height,
      blockTimestamp: sortedBlocks[i].timestamp,
      actualInterval,
      predictedMedian,
      error,
      percentError,
      absError: Math.abs(error),
    });
  }
  
  // Handle case where no valid predictions were made
  if (errors.length === 0) {
    if (DEBUG_POISSON) {
      console.warn('No valid predictions made during backtesting');
    }
    return null;
  }
  
  // Compute aggregate metrics
  const errorValues = errors.map(e => e.error);
  const absErrorValues = errors.map(e => e.absError);
  
  // Mean Absolute Error: MAE = (1/n)Σ|eᵢ|
  const mae = absErrorValues.reduce((sum, err) => sum + err, 0) / absErrorValues.length;
  
  // Root Mean Square Error: RMSE = sqrt((1/n)Σeᵢ²)
  const squaredErrors = errorValues.map(e => e * e);
  const meanSquaredError = squaredErrors.reduce((sum, sqErr) => sum + sqErr, 0) / squaredErrors.length;
  const rmse = Math.sqrt(meanSquaredError);
  
  // Mean signed error (bias indicator)
  const meanError = errorValues.reduce((sum, err) => sum + err, 0) / errorValues.length;
  
  // Calibration: percentage of actual intervals < predicted median
  // For well-calibrated exponential model, should be ~50%
  const belowMedianCount = errors.filter(e => e.actualInterval < e.predictedMedian).length;
  const calibration = (belowMedianCount / errors.length) * 100;
  
  // Find best and worst predictions
  const bestPrediction = errors.reduce((best, current) => 
    current.absError < best.absError ? current : best, errors[0]);
  const worstPrediction = errors.reduce((worst, current) => 
    current.absError > worst.absError ? current : worst, errors[0]);
  
  // Calculate median error
  const sortedAbsErrors = [...absErrorValues].sort((a, b) => a - b);
  const medianError = sortedAbsErrors.length % 2 === 0
    ? (sortedAbsErrors[sortedAbsErrors.length / 2 - 1] + sortedAbsErrors[sortedAbsErrors.length / 2]) / 2
    : sortedAbsErrors[Math.floor(sortedAbsErrors.length / 2)];
  
  const result = {
    metrics: {
      mae,
      rmse,
      calibration,
      totalPredictions: errors.length,
      meanError,
    },
    errors,
    summary: {
      bestPrediction,
      worstPrediction,
      medianError,
    },
  };
  
  if (DEBUG_POISSON) {
    console.log('Backtesting results:', {
      mae: result.metrics.mae,
      rmse: result.metrics.rmse,
      calibration: result.metrics.calibration,
      totalPredictions: result.metrics.totalPredictions,
      errorsCount: result.errors.length,
    });
  }
  
  return result;
}

/**
 * Optimize model parameters via grid search to minimize backtest MAE
 * 
 * Performs grid search over candidate parameter combinations (outlierPThreshold × hashrateWeight)
 * to find the parameter set that minimizes Mean Absolute Error (MAE) from backtesting.
 * 
 * Mathematical approach:
 * - Tests all combinations of outlierPThreshold × hashrateWeight
 * - For each combination:
 *   1. Runs outlier detection with given pThreshold
 *   2. Filters outliers from blocks
 *   3. Backtests predictions using empirical lambda
 *   4. Computes MAE = (1/n)Σ|actual_i - predicted_i|
 * - Returns parameter set with lowest MAE
 * 
 * @param {PoolBlock[]} blocks - Array of raw blocks before outlier filtering
 * @param {PoolInfo} poolInfo - Pool information for lambda calculation
 * @param {Object} candidateParams - Grid search space:
 *   - {number[]} outlierPThresholds - Array of p-threshold values (e.g., [0.01, 0.02, ..., 0.1])
 *   - {number[]} hashrateWeights - Array of hashrate weight values (e.g., [0, 0.1, ..., 1.0])
 * @param {Object} [options] - Optional configuration:
 *   - {number} maxIterations - Max outlier detection iterations (default 3)
 *   - {Function} progressCallback - Called with (current, total, bestSoFar) for progress updates
 * @returns {{
 *   bestParams: { outlierPThreshold: number, hashrateWeight: number },
 *   bestMAE: number,
 *   allResults: Array<{
 *     pThreshold: number,
 *     hashrateWeight: number,
 *     mae: number,
 *     rmse: number,
 *     calibration: number,
 *     outliersCount: number
 *   }>,
 *   totalCombinations: number
 * } | null} Optimization results, or null if insufficient data or all combinations fail
 * 
 * @example
 * const candidateParams = {
 *   outlierPThresholds: [0.01, 0.03, 0.05, 0.07, 0.1],
 *   hashrateWeights: [0.3, 0.4, 0.5, 0.6, 0.7],
 * };
 * 
 * const result = optimizeModelParams(blocks, poolInfo, candidateParams, {
 *   maxIterations: 3,
 *   progressCallback: (current, total, bestMAE) => {
 *     console.log(`Progress: ${current}/${total}, best MAE: ${bestMAE?.toFixed(2)}s`);
 *   },
 * });
 * 
 * if (result) {
 *   console.log('Optimal parameters:', result.bestParams);
 *   console.log('Best MAE:', result.bestMAE, 'seconds');
 * }
 */
export function optimizeModelParams(blocks, poolInfo, candidateParams, options = {}) {
  // Input validation
  if (!Array.isArray(blocks) || blocks.length < 10) {
    if (DEBUG_POISSON) {
      console.warn('Insufficient blocks for optimization:', blocks.length);
    }
    return null;
  }
  
  if (!poolInfo) {
    throw new Error('Pool info is required for optimization');
  }
  
  if (!candidateParams || !Array.isArray(candidateParams.outlierPThresholds) || 
      candidateParams.outlierPThresholds.length === 0 ||
      !Array.isArray(candidateParams.hashrateWeights) || 
      candidateParams.hashrateWeights.length === 0) {
    throw new Error('candidateParams must contain non-empty outlierPThresholds and hashrateWeights arrays');
  }
  
  const { maxIterations = 3, progressCallback } = options;
  
  // Initialize tracking variables
  let bestParams = null;
  let bestMAE = Infinity;
  const results = [];
  const totalCombinations = candidateParams.outlierPThresholds.length * candidateParams.hashrateWeights.length;
  let currentCombination = 0;
  
  // Nested loop over all parameter combinations
  for (const pThreshold of candidateParams.outlierPThresholds) {
    for (const hashrateWeight of candidateParams.hashrateWeights) {
      currentCombination++;
      
      try {
        // Compute initial lambda using current hashrateWeight
        const blockShareWeight = 1 - hashrateWeight;
        const initialLambda = calculatePoolBlockRateFromPoolInfo(poolInfo, hashrateWeight, blockShareWeight);
        
        // Skip if initialLambda is invalid
        if (initialLambda <= 0 || !Number.isFinite(initialLambda)) {
          if (DEBUG_POISSON) {
            console.warn(`Skipping combination (pThreshold=${pThreshold}, hashrateWeight=${hashrateWeight}): invalid lambda`);
          }
          continue;
        }
        
        // Run outlier detection with current pThreshold
        const outlierResult = detectOutliers(blocks, initialLambda, pThreshold, maxIterations);
        const filteredBlocks = outlierResult.blocks.filter(b => !b.isOutlier);
        
        // Skip if insufficient filtered blocks
        if (filteredBlocks.length < 2) {
          if (DEBUG_POISSON) {
            console.warn(`Skipping combination (pThreshold=${pThreshold}, hashrateWeight=${hashrateWeight}): insufficient filtered blocks (${filteredBlocks.length})`);
          }
          continue;
        }
        
        // Define lambda function for backtesting (uses empirical lambda from prior blocks)
        const lambdaFunc = (priorBlocks) => {
          if (priorBlocks.length < 2) return null;
          const empiricalStats = calculateEmpiricalLambda(priorBlocks);
          return empiricalStats?.lambda || null;
        };
        
        // Run backtesting
        const backtestResult = backtestAccuracy(filteredBlocks, lambdaFunc);
        
        // Skip if backtesting failed
        if (!backtestResult) {
          if (DEBUG_POISSON) {
            console.warn(`Skipping combination (pThreshold=${pThreshold}, hashrateWeight=${hashrateWeight}): backtesting failed`);
          }
          continue;
        }
        
        // Extract metrics
        const mae = backtestResult.metrics.mae;
        const rmse = backtestResult.metrics.rmse;
        const calibration = backtestResult.metrics.calibration;
        const outliersCount = outlierResult.outliersCount;
        
        // Store result
        results.push({
          pThreshold,
          hashrateWeight,
          mae,
          rmse,
          calibration,
          outliersCount,
        });
        
        // Update best if this combination has lower MAE
        if (mae < bestMAE) {
          bestMAE = mae;
          bestParams = {
            outlierPThreshold: pThreshold,
            hashrateWeight,
          };
        }
        
        // Call progress callback if provided
        if (progressCallback) {
          progressCallback(currentCombination, totalCombinations, bestMAE);
        }
      } catch (err) {
        if (DEBUG_POISSON) {
          console.warn(`Error in optimization combination (pThreshold=${pThreshold}, hashrateWeight=${hashrateWeight}):`, err);
        }
        // Continue to next combination on error
        continue;
      }
    }
  }
  
  // Return null if no valid results
  if (results.length === 0) {
    if (DEBUG_POISSON) {
      console.warn('Optimization failed: no valid parameter combinations found');
    }
    return null;
  }
  
  // Sort results by MAE ascending
  results.sort((a, b) => a.mae - b.mae);
  
  return {
    bestParams,
    bestMAE,
    allResults: results,
    totalCombinations: results.length,
  };
}

